import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { canAccessDept } from "@/lib/constants";
import { ArrowUpRight } from "lucide-react";

const DEPTS = [
  { code: "01", key: "architecture", label: "ARCHITECTURE" },
  { code: "02", key: "3d_visualisation", label: "3D VISUALIZATION" },
  { code: "03", key: "construction", label: "CONSTRUCTION" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState({});
  const [site, setSite] = useState({});
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);

  // Only ever render the background video when we are EXACTLY on the
  // portal landing route. Belt-and-suspenders: Dashboard is already only
  // mounted at /portal via router, but guarding by pathname prevents any
  // edge-case leakage (e.g. transitions, stale renders after route swap).
  const isDashboardRoute = location.pathname === "/portal" || location.pathname === "/portal/";

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data || {})).catch(() => {});
    api.get("/site/content").then((r) => setSite(r.data || {})).catch(() => {});
  }, []);

  const videoEnabled = isDashboardRoute && !!(site.portal_video_enabled && site.portal_video_url);
  const videoOpacity = typeof site.portal_video_opacity === "number" ? site.portal_video_opacity : 0.4;
  const overlayOpacity = typeof site.portal_overlay_opacity === "number" ? site.portal_overlay_opacity : 0.35;
  const playbackRate = typeof site.portal_video_playback_rate === "number" ? site.portal_video_playback_rate : 1.0;

  // Apply playback rate when video loads or setting changes
  useEffect(() => {
    if (videoRef.current) {
      try { videoRef.current.playbackRate = playbackRate || 1; } catch (_) {}
    }
  }, [playbackRate, site.portal_video_url]);

  // Explicit cleanup: pause the video and release its source on unmount so
  // no background playback / buffering can leak to other portal pages.
  useEffect(() => {
    const v = videoRef.current;
    return () => {
      if (v) {
        try {
          v.pause();
          v.removeAttribute("src");
          v.load();
        } catch (_) {}
      }
    };
  }, []);

  return (
    <div className="relative min-h-screen bg-white overflow-hidden" data-testid="portal-dashboard">
      {/* Full-viewport fixed video layer */}
      {videoEnabled && (
        <div
          className="fixed inset-0 pointer-events-none z-0 motion-reduce:hidden"
          aria-hidden="true"
          data-testid="portal-video-layer"
        >
          <video
            key={site.portal_video_url}
            ref={videoRef}
            src={site.portal_video_url}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className={`w-full h-full object-cover transition-opacity duration-700 ${videoReady ? "opacity-100" : "opacity-0"}`}
            style={{ opacity: videoReady ? videoOpacity : 0 }}
            onCanPlay={() => setVideoReady(true)}
            onError={() => setVideoReady(false)}
          />
          {/* Subtle white overlay for readability */}
          <div
            className="absolute inset-0 bg-white pointer-events-none"
            style={{ opacity: overlayOpacity }}
          />
        </div>
      )}

      {/* Content — always shown, doesn't wait for video */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top meta bar */}
        <div className="px-6 lg:px-12 pt-8 flex items-center justify-between font-cg text-[10px] tracking-[0.32em] uppercase text-ink/70">
          <span data-testid="portal-top-meta-left">AS · STUDIOS / STAFF PORTAL</span>
          <span data-testid="portal-top-meta-right">
            {new Date().toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" }).toUpperCase()}
          </span>
        </div>

        {/* Center block */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 lg:px-12 py-16">
          {/* Heading */}
          <div className="text-center mb-16 lg:mb-24" data-testid="portal-heading-block">
            <div className="font-cg text-xs sm:text-sm tracking-[0.55em] text-ink/60 mb-4" data-testid="portal-subheading">
              AS DESIGN STUDIOS  /  STAFF PORTAL
            </div>
            <h1 className="font-cg font-light text-3xl sm:text-4xl lg:text-5xl tracking-[0.35em] text-ink" data-testid="portal-heading">
              SELECT DEPARTMENT
            </h1>
            <div className="mt-6 flex items-center justify-center gap-3 font-cg text-[10px] tracking-[0.3em] uppercase text-ink/50">
              <span className="h-px w-8 bg-ink/40" />
              <span>Welcome, {user?.name?.split(" ")[0] || "Staff"}</span>
              <span className="h-px w-8 bg-ink/40" />
            </div>
          </div>

          {/* Tiles */}
          <div
            className="w-full max-w-5xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12"
            data-testid="dept-tiles-row"
          >
            {DEPTS.map((d) => {
              const accessible = canAccessDept(user, d.key);
              return (
                <DepartmentTile
                  key={d.key}
                  dept={d}
                  accessible={accessible}
                />
              );
            })}
          </div>
        </div>

        {/* Bottom meta strip */}
        <div className="px-6 lg:px-12 pb-6 flex items-center justify-between font-cg text-[10px] tracking-[0.32em] uppercase text-ink/50">
          <span data-testid="portal-bottom-meta-left">STATUS · ONLINE</span>
          <div className="flex items-center gap-4">
            <span>PROJ · {stats.total_projects ?? 0}</span>
            <span>TEAM · {stats.team_members ?? 0}</span>
            <span>NEW · {stats.new_enquiries ?? 0}</span>
          </div>
          <Link to="/portal/enquiries" data-testid="dashboard-view-enquiries" className="hover:text-ink transition-colors">
            ENQUIRIES →
          </Link>
        </div>
      </div>
    </div>
  );
}

function DepartmentTile({ dept, accessible }) {
  return (
    <Link
      to={accessible ? `/portal/departments/${dept.key}` : "#"}
      onClick={(e) => { if (!accessible) e.preventDefault(); }}
      aria-disabled={!accessible}
      data-testid={`dept-tile-${dept.key}`}
      className={`group relative aspect-square block border border-ink text-ink transition-all duration-300 ease-out will-change-transform
        ${accessible
          ? "hover:scale-[1.02] hover:-translate-y-0.5 hover:border-[1.5px] focus:scale-[1.02] focus:-translate-y-0.5 focus:outline-none"
          : "opacity-40 cursor-not-allowed"}
      `}
      style={{ backgroundColor: "transparent" }}
    >
      {/* Corner brackets — animate in on hover */}
      <Corner position="tl" />
      <Corner position="tr" />
      <Corner position="bl" />
      <Corner position="br" />

      {/* Top-left department code */}
      <div className="absolute top-4 left-4 font-cg text-[10px] sm:text-[11px] tracking-[0.32em] text-ink" data-testid={`dept-code-${dept.key}`}>
        {dept.code} <span className="text-ink/40">/</span> <span className="text-ink/60">DEPT</span>
      </div>

      {/* Center — department name */}
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <span
          className="font-cg font-normal text-center text-ink text-lg sm:text-xl lg:text-2xl transition-[letter-spacing] duration-500 ease-out tracking-[0.16em] group-hover:tracking-[0.28em]"
          data-testid={`dept-name-${dept.key}`}
        >
          {dept.label}
        </span>
      </div>

      {/* Bottom-right arrow — appears on hover */}
      <div className="absolute bottom-4 right-4 flex items-center gap-1.5 font-cg text-[10px] tracking-[0.28em] text-ink opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
        <span>ENTER</span>
        <ArrowUpRight size={12} strokeWidth={1.6} />
      </div>

      {/* Coordinate line — bottom-left, appears on hover */}
      <div className="absolute bottom-4 left-4 font-cg text-[9px] tracking-[0.32em] text-ink/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-75" data-testid={`dept-coord-${dept.key}`}>
        {accessible ? "AUTHORISED" : "RESTRICTED"}
      </div>

      {/* Animated top line — sweeps in on hover */}
      <span className="absolute top-0 left-0 h-px bg-ink w-0 group-hover:w-full transition-[width] duration-500 ease-out" />
    </Link>
  );
}

function Corner({ position }) {
  const map = {
    tl: "top-0 left-0 border-t border-l -translate-x-0 -translate-y-0 group-hover:-translate-x-1 group-hover:-translate-y-1",
    tr: "top-0 right-0 border-t border-r translate-x-0 -translate-y-0 group-hover:translate-x-1 group-hover:-translate-y-1",
    bl: "bottom-0 left-0 border-b border-l -translate-x-0 translate-y-0 group-hover:-translate-x-1 group-hover:translate-y-1",
    br: "bottom-0 right-0 border-b border-r translate-x-0 translate-y-0 group-hover:translate-x-1 group-hover:translate-y-1",
  };
  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute w-3 h-3 border-ink transition-all duration-300 opacity-0 group-hover:opacity-100 ${map[position]}`}
    />
  );
}
